import React, { useCallback, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import ErrorIcon from '@mui/icons-material/Error';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { WALLET_TYPES } from './constants';

const WalletCard = React.memo(({ wallet, onSetPrimary, onEdit, onDelete }) => {
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const getWalletDetails = useCallback((w) => {
    // Handle Plaid-linked accounts
    if (w.source === 'plaid') {
      return `${w.institution_name || 'Bank'} ${w.account_last_4 || ''}`;
    }

    // Handle manual entries with unified format
    switch (w.wallet_type) {
      case "paypal":
        return w.paypal_email;
      case "wise":
        return `${w.wise_email}${w.wise_currency ? ` (${w.wise_currency})` : ""}`;
      case "bank_ach":
        return `${w.institution_name || w.bank_name || "Bank"} ${w.account_last_4 || w.bank_account_number || ''}`;
      case "crypto":
        return `${w.crypto_currency || ""} ${w.crypto_wallet_address ? `${String(w.crypto_wallet_address).slice(0, 8)}…` : ""}`;
      case "stripe":
        return w.stripe_status || "Stripe Account";
      default:
        return w.nickname || "Payment method";
    }
  }, []);

  const getStatusColor = useCallback((status) => {
    switch (status) {
      case "verified":
        return "success";
      case "pending":
        return "warning";
      case "failed":
        return "error";
      default:
        return "default";
    }
  }, []);

  const handleEdit = useCallback(() => {
    onEdit(wallet);
  }, [onEdit, wallet]);

  const handleDeleteClick = useCallback(() => {
    setConfirmDeleteOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    setConfirmDeleteOpen(false);
    onDelete(wallet.id);
  }, [onDelete, wallet.id]);

  const handleCancelDelete = useCallback(() => {
    setConfirmDeleteOpen(false);
  }, []);

  const handleSetPrimary = useCallback(() => {
    onSetPrimary(wallet.id);
  }, [onSetPrimary, wallet.id]);

  const walletType = useMemo(() => 
    WALLET_TYPES.find(t => t.value === wallet.wallet_type), [wallet.wallet_type]
  );

  return (
    <Card
      sx={{
        mb: 2,
        border: wallet.is_primary ? 2 : 1,
        borderColor: wallet.is_primary ? "primary.main" : "divider",
        position: "relative",
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: 4,
          transform: "translateY(-2px)",
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
            <Typography variant="h6" sx={{ fontSize: "1.5rem" }}>
              {walletType?.icon || "💳"}
            </Typography>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Typography variant="h6" sx={{ fontSize: "1.1rem", fontWeight: 600 }}>
                  {wallet.institution_name || wallet.nickname || walletType?.label || "Payment Method"}
                </Typography>
                {wallet.is_primary && (
                  <Chip icon={<StarIcon />} label="Primary" size="small" color="primary" />
                )}
              </Box>
              {wallet.nickname && wallet.institution_name && wallet.nickname !== wallet.institution_name && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {wallet.nickname}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {getWalletDetails(wallet)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label={wallet.verification_status || "unverified"}
                  color={getStatusColor(wallet.verification_status)}
                  icon={
                    wallet.verification_status === "verified" ? (
                      <VerifiedIcon />
                    ) : (
                      <ErrorIcon />
                    )
                  }
                />
                {wallet.source === 'plaid' && (
                  <Chip
                    label="Plaid Verified"
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            {!wallet.is_primary && (
              <Tooltip title="Set as primary">
                <IconButton 
                  size="small" 
                  onClick={handleSetPrimary}
                  sx={{ color: "text.secondary" }}
                >
                  <StarBorderIcon />
                </IconButton>
              </Tooltip>
            )}
            {wallet.source !== 'plaid' && (
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={handleEdit}
                  sx={{ color: "text.secondary" }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={handleDeleteClick}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>

      <Dialog
        open={confirmDeleteOpen}
        onClose={handleCancelDelete}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove this payment method?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            You are about to remove{' '}
            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {wallet.institution_name || wallet.nickname || walletType?.label || 'this payment method'}
            </Box>
            . This action can&apos;t be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
});

export default WalletCard;